import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import useDebounce from "../Hooks/useDebounce";
import { useNavigate, useParams } from "react-router-dom";
import { createEntity, fetchEntityList, getEntity, updateEntity } from "../services/api";
import FeatureConfiguration from "../components/FeatureConfiguration";
import { USER_ROLES } from "../lib/utils/constant";
import {
  sanitizeLocationFieldInput,
  validateLocationField,
} from "../lib/utils/validation";

const AddEditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const [entitiesList, setEntitiesList] = useState([]);
  const [steps, setSteps] = useState(1);
  const [isFetchingZip, setIsFetchingZip] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [resultModal, setResultModal] = useState({
    show: false,
    variant: "success", // success | danger
    title: "",
    message: "",
    shouldNavigateOnClose: false,
  });
  const [confirmCancelModalOpen, setConfirmCancelModalOpen] = useState(false);
  // Tracks the *parent entity's* feature flags — used only for visibility,
  // never mixed with the client's toggle state in formData.
  const [parentEntityConfig, setParentEntityConfig] = useState(null);
  const [formData, setFormData] = useState({
    parentId: "",
    name: "",
    abbreviation: "",
    address: "",
    zipCode: "",
    city: "",
    state: "",
    country: "",
    supportEmail: "",
    websiteUrl: "",
    chargeToCustomer: false,
    enableCustomEmailTemplate: false,
    enableAdmin: true,
    enableManager: true,
    enableCaseManager: true,
    caseManagerAccessMode: "ALL",
    enableEntityAssignedRecordId: false,
    defaultEntityAdmin: {
      firstName: "",
      lastName: "",
      emailAddress: "",
    },
    firstName: "",
    lastName: "",
    email: "",
  });
  const debouncedzipCode = useDebounce(formData.zipCode, 500);

  const validateNameLike = (value) => {
    const v = String(value || "").trim();
    if (v.length < 2) return "Must be at least 2 characters.";
    if (!/^[A-Za-z]+(?: [A-Za-z]+)*$/.test(v))
      return "Only alphabets are allowed.";
    return null;
  };

  const validateAbbreviation = (value) => {
    const v = String(value || "").trim();
    if (!v) return "Abbreviation is required.";
    if (!/^[A-Za-z]+$/.test(v)) return "Only alphabets are allowed.";
    return null;
  };

  const validateEmail = (value) => {
    const v = String(value || "").trim();
    if (!v) return "Email is required.";
    // Reasonable email validation for UI (backend should validate too)
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))
      return "Enter a valid email address.";
    return null;
  };

  const validateWebsiteUrl = (value) => {
    const v = String(value || "").trim();
    if (!v) return null; // optional
    if (!/^(https?:\/\/)([A-Za-z0-9-]+\.)+[A-Za-z]{2,}(\/.*)?$/.test(v))
      return "Enter a valid URL starting with http:// or https:// (must include a domain with a dot).";
    return null;
  };

  const validateSupportEmail = (value) => {
    const v = String(value || "").trim();
    if (!v) return null; // optional
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))
      return "Enter a valid email address.";
    return null;
  };

  const validateStep1 = (data) => {
    const nextErrors = {};
    if (!String(data.parentId || "").trim()) nextErrors.parentId = "Entity is required.";
    const nameErr = validateNameLike(data.name);
    if (nameErr) nextErrors.name = nameErr;

    const abbrErr = validateAbbreviation(data.abbreviation);
    if (abbrErr) nextErrors.abbreviation = abbrErr;

    if (!String(data.address || "").trim()) nextErrors.address = "Address is required.";

    const zipErr = validateLocationField("zipCode", data.zipCode);
    if (zipErr) nextErrors.zipCode = zipErr;

    const cityErr = validateLocationField("city", data.city);
    if (cityErr) nextErrors.city = cityErr;

    const stateErr = validateLocationField("state", data.state);
    if (stateErr) nextErrors.state = stateErr;

    const countryErr = validateLocationField("country", data.country);
    if (countryErr) nextErrors.country = countryErr;

    const supportErr = validateSupportEmail(data.supportEmail);
    if (supportErr) nextErrors.supportEmail = supportErr;

    const websiteErr = validateWebsiteUrl(data.websiteUrl);
    if (websiteErr) nextErrors.websiteUrl = websiteErr;

    return nextErrors;
  };

  const validateStep2 = (data) => {
    const nextErrors = {};
    const fnErr = validateNameLike(data.firstName);
    if (fnErr) nextErrors.firstName = fnErr;

    const lnErr = validateNameLike(data.lastName);
    if (lnErr) nextErrors.lastName = lnErr;

    const emailErr = validateEmail(data.email);
    if (emailErr) nextErrors.email = emailErr;

    const supportEmail = String(data.supportEmail || "").trim().toLowerCase();
    const adminEmail = String(data.email || "").trim().toLowerCase();
    if (supportEmail && adminEmail && supportEmail === adminEmail) {
      nextErrors.email = "Admin email cannot be the same as support email.";
    }

    return nextErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue =
      type === "checkbox"
        ? checked
        : ["zipCode", "city", "state", "country"].includes(name)
          ? sanitizeLocationFieldInput(name, value)
          : value;

    if (name === "parentId") {
      const entity = entitiesList.find((en) => String(en.id) === String(value));
      setParentEntityConfig(entity ?? null);
      // Show/hide is driven by the entity flag; the client toggle always starts OFF
      if (entity) {
        setFormData((prev) => ({
          ...prev,
          chargeToCustomer: false,
          enableCustomEmailTemplate: false,
          enableEntityAssignedRecordId: false,
          caseManagerAccessMode: entity.caseManagerAccessMode || "ALL",
        }));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
    setErrors((prev) => {
      if (!prev?.[name]) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const setFlag = (key, checked) => {
    setFormData((prev) => ({ ...prev, [key]: !!checked }));
    setErrors((prev) => {
      if (!prev?.[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const setAccessLevel = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev?.[key]) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  // Always use parentEntityConfig for visibility — never formData.
  // This prevents the circular issue where toggling a feature OFF would hide it.
  const entityFlags = parentEntityConfig;

  const allFeatureSections = [
    {
      id: "billing",
      title: "Billing & Subscription Settings",
      description: "Configure subscription plans and how costs are billed to customers.",
      items: [
        {
          key: "chargeToCustomer",
          label: "Charge to Customer",
          helper: "Customer's account billed for subscriptions.",
          disabled: true,
        },
        {
          key: "enableCustomEmailTemplate",
          label: "Custom Email Templates",
          helper: "Enable custom email templates for communications.",
        },
      ],
    },
    {
      id: "caseManager",
      title: "Manage Care Team Roles",
      description: "Control which roles are active and their user access permissions.",
      items: [
        {
          key: "enableAdmin",
          label: "Admin",
          helper: "Admin access for Contracted Entity/Client: Manage users, and system settings.",
          disabled: true,
        },
        {
          key: "enableManager",
          label: "Manager",
          helper: "Coordinates care and manages users within assigned Client.",
          disabled: true,
        },
        {
          key: "enableCaseManager",
          label: "Case Manager/Care Representative (CRE)",
          helper: "Grant case managers access and define their device user visibility.",
          disabled: true,
          accessLevel: {
            key: "caseManagerAccessMode",
            label: "DEFINE DEVICE USER ACCESS LEVEL",
            defaultValue: "ALL",
            options: [
              { label: "All", value: "ALL" },
              { label: "Group", value: "ASSIGNED" },
            ],
          },
        },
      ],
    },
    {
      id: "security",
      title: "Security Settings",
      description: "Security controls for record access and identifiers.",
      items: [
        {
          key: "enableEntityAssignedRecordId",
          label: "Entity Assigned Record ID",
          helper: "Use entity-assigned record IDs where applicable.",
        },
      ],
    },
  ];

  // Keys controlled by a boolean entity flag (true = show, false = hide).
  const entityGatedKeys = ["chargeToCustomer", "enableCustomEmailTemplate", "enableEntityAssignedRecordId"];

  const featureSections = !entityFlags
    ? [] // no entity selected yet → hide the whole block
    : allFeatureSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (!entityGatedKeys.includes(item.key)) return true;
            return !!entityFlags[item.key];
          }),
        }))
        .filter((section) => section.items.length > 0);


  const fetchzipCodeData = useCallback(async (zip) => {
    if (!zip || zip.length < 5) return;
    setIsFetchingZip(true);
    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!response.ok) throw new Error("zipCode not found");
      const data = await response.json();
      const place = data.places[0];
      setFormData((prev) => ({
        ...prev,
        city: place["place name"],
        state: place["state abbreviation"],
        country: data["country abbreviation"],
      }));
    } catch (error) {
      console.error("zipCode fetch error:", error);
    } finally {
      setIsFetchingZip(false);
    }
  }, []);

  const loadEntities = async () => {
    try {
      const response = await fetchEntityList();
      if (response.data && response.data.success && response.data.data?.items) {
        setEntitiesList(response.data.data.items);
      }
    } catch (error) {
      console.error("Error loading entities:", error);
    }
  };

  const prefillData = async () => {
    try {
      const response = await getEntity(id);
      if (response.data && response.data.success && response.data.data) {
        const data = response.data.data;
        setFormData({
          ...data,
          enableAdmin: true,
          enableManager: true,
          enableCaseManager: true,
          caseManagerAccessMode: data?.caseManagerAccessMode || "ALL",
          defaultEntityAdmin: data?.defaultEntityAdmin || {
            firstName: "",
            lastName: "",
            emailAddress: "",
          },
          // keep form fields controlled via top-level state (step 2)
          firstName: data?.defaultEntityAdmin?.firstName ?? "",
          lastName: data?.defaultEntityAdmin?.lastName ?? "",
          email: data?.defaultEntityAdmin?.emailAddress ?? "",
        });
      }
    } catch (error) {
      console.error("Error loading entity:", error);
    }
  };

  useEffect(() => {
    if (id) prefillData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Edit mode: once entitiesList is ready and formData.parentId is set,
  // resolve the parent entity so visibility is driven by the entity's flags,
  // not the client's toggle state.
  useEffect(() => {
    if (!id || !formData.parentId || entitiesList.length === 0) return;
    if (parentEntityConfig) return; // already resolved
    const entity = entitiesList.find((en) => String(en.id) === String(formData.parentId));
    if (entity) setParentEntityConfig(entity);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, formData.parentId, entitiesList]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (steps === 1) {
      const step1Errors = validateStep1(formData);
      if (Object.keys(step1Errors).length) {
        setErrors(step1Errors);
        return;
      }
      setSteps((prev) => prev + 1);
      return;
    }
    const step2Errors = validateStep2(formData);
    if (Object.keys(step2Errors).length) {
      setErrors(step2Errors);
      return;
    }
    const payload = {
      entity: {
        name: formData.name,
        abbreviation: formData.abbreviation,
        accountType: USER_ROLES.CLIENT,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        zipCode: formData.zipCode,
        state: formData.state,
        supportEmail: formData.supportEmail ? formData.supportEmail : null,
        website: formData.websiteUrl ? formData.websiteUrl : null,
        parentId: currentUser?.role?.code !== USER_ROLES.SYS_ADMIN ? currentUser?.entityMaster?.id : formData.parentId,
        defaultEntityAdmin: {
          emailAddress: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
        chargeToCustomer: !!formData.chargeToCustomer,
        enableCustomEmailTemplate: !!formData.enableCustomEmailTemplate,
        enableAdmin: true,
        enableManager: true,
        enableCaseManager: true,
        caseManagerAccessMode: formData.caseManagerAccessMode || "ALL",
        enableEntityAssignedRecordId: !!formData.enableEntityAssignedRecordId,
      },
    };
    const updatePayload = payload.entity;
    updatePayload.defaultEntityAdmin.id = formData.defaultEntityAdmin.id;
    setIsSaving(true);
    try {
      const res = id ? await updateEntity(id, updatePayload) : await createEntity(payload);
      setResultModal({
        show: true,
        variant: "success",
        title: `Client ${id ? "Updated" : "Created"}`,
        message:
          res?.data?.message ||
          `Client has been ${id ? "updated" : "created"} successfully.`,
        shouldNavigateOnClose: true,
      });
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        `Failed to ${id ? "update" : "create"} client. Please try again.`;
      console.error(`${id ? "update" : "create"} client failed:`, error);
      setResultModal({
        show: true,
        variant: "danger",
        title: `Failed to ${id ? "update" : "create"} client`,
        message: msg,
        shouldNavigateOnClose: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetFormToInitial = () => {
    setSteps(1);
    setErrors({});
    setFormData({
      name: "",
      abbreviation: "",
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      supportEmail: "",
      websiteUrl: "",
      chargeToCustomer: true,
      enableCustomEmailTemplate: false,
      enableCaseManager: true,
      caseManagerAccessMode: "ALL",
      enableEntityAssignedRecordId: false,
      firstName: "",
      lastName: "",
      email: "",
    });
  };

  const handleCancelClick = () => {
    setConfirmCancelModalOpen(true);
  };

  const confirmCancel = () => {
    setConfirmCancelModalOpen(false);
    resetFormToInitial();
    navigate("/client");
  };

  const renderStep1 = () => {
    return (
      <>
        <Row className="mb-3">
          {currentUser?.role?.code === USER_ROLES.SYS_ADMIN && <Form.Group as={Col} md={4} className="mb-3" controlId="parentId">
            <Form.Label>Select Entity*</Form.Label>
            <Form.Select
              required
              name="parentId"
              value={formData.parentId}
              onChange={handleChange}
              isInvalid={!!errors.parentId}
              disabled={!!id}
            >
              <option value="">Select...</option>
              {entitiesList?.map(e => (<option key={e.id} value={e.id}>{e.name}</option>))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.parentId}
            </Form.Control.Feedback>
            </Form.Group>}
          <Form.Group as={Col} md={4} controlId="firstName">
            <Form.Label>NAME*</Form.Label>
            <Form.Control
              required
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter Name"
              isInvalid={!!errors.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={3} controlId="lastName">
            <Form.Label>Abbreviation*</Form.Label>
            <Form.Control
              required
              name="abbreviation"
              value={formData.abbreviation}
              onChange={handleChange}
              placeholder="Enter Abbreviation"
              isInvalid={!!errors.abbreviation}
              disabled={!!id}
            />
            <Form.Control.Feedback type="invalid">
              {errors.abbreviation}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={6} controlId="address">
            <Form.Label>Address*</Form.Label>
            <Form.Control
              required
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter Address"
              isInvalid={!!errors.address}
            />
            <Form.Control.Feedback type="invalid">
              {errors.address}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-3">
        <Form.Group as={Col} md={3} controlId="zipCode">
            <Form.Label>zipCode*</Form.Label>
            <Form.Control
              required
              type="text"
              name="zipCode"
              value={formData.zipCode}
              onChange={handleChange}
              placeholder="Enter zipCode"
              isInvalid={!!errors.zipCode}
              maxLength={20}
            />
            {isFetchingZip && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}

            <Form.Control.Feedback type="invalid">
              {errors.zipCode}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={5} controlId="city">
            <Form.Label>City*</Form.Label>
            <Form.Control
              required
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="Enter City"
              isInvalid={!!errors.city}
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={4} controlId="state">
            <Form.Label>State*</Form.Label>
            <Form.Control
              required
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter State"
              isInvalid={!!errors.state}
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid">
              {errors.state}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-3">
        <Form.Group as={Col} md={3} controlId="country">
            <Form.Label>Country*</Form.Label>
            <Form.Control
              required
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Enter Country"
              isInvalid={!!errors.country}
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid">
              {errors.country}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={4} controlId="supportEmail">  
            <Form.Label>Support Email</Form.Label>
            <Form.Control
              type="email"
              name="supportEmail"
              value={formData.supportEmail}
              onChange={handleChange}
              placeholder="Enter Support Email"
              isInvalid={!!errors.supportEmail}
            />
            <Form.Control.Feedback type="invalid">
              {errors.supportEmail}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={5} controlId="websiteUrl">
            <Form.Label>Website URL</Form.Label>
            <Form.Control
              type="text"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleChange}
              placeholder="Enter Website URL"
              isInvalid={!!errors.websiteUrl}
            />
            <Form.Control.Feedback type="invalid">
              {errors.websiteUrl}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        {featureSections.length > 0 && (
          <Row className="mb-3">
            <Col md={12}>
              <FeatureConfiguration
                sections={featureSections}
                values={formData}
                onToggle={setFlag}
                onAccessLevelChange={setAccessLevel}
              />
            </Col>
          </Row>
        )}
      </>
    );
  };

  const renderStep2 = () => {
    return (
      <>
        <Row className="mb-3">
          <Form.Group as={Col} md={4} controlId="firstName">
            <Form.Label>FIRST NAME*</Form.Label>
            <Form.Control
              required
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter First Name"
              isInvalid={!!errors.firstName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={4} controlId="lastName">
            <Form.Label>LAST NAME*</Form.Label>
            <Form.Control
              required
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter Last Name"
              isInvalid={!!errors.lastName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md={4} controlId="email">
            <Form.Label>EMAIL*</Form.Label>
            <Form.Control
              required
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email"
              isInvalid={!!errors.email}
              disabled={!!id}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      </>
    );
  };
  // Effect for Personal Zip
  useEffect(() => {
    if (debouncedzipCode) fetchzipCodeData(debouncedzipCode, false);
  }, [debouncedzipCode, fetchzipCodeData]);

  useEffect(() => {
    loadEntities();
  }, []);
  return (
    <Container fluid className="px-0">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-white p-3">
              <Row className="justify-content-between align-items-center">
                <Col>
                  <h1 className="h3 mb-0 text-primary">{`${id ? "EDIT" : "CREATE"} CLIENT`}</h1>
                </Col>
                {/* <Col className="text-end"><span className="text-danger">*required fields</span></Col> */}
              </Row>
            </Card.Header>
            <Card.Body className="p-4 p-md-5">
              <Form onSubmit={handleSubmit}>
                {steps === 1 && renderStep1()}
                {steps === 2 && renderStep2()}
                <Row className="mb-3 align-items-center">
                  <Col>
                    {steps > 1 && (
                      <Button
                        variant="light"
                        className="border"
                        type="button"
                        onClick={() => setSteps((prev) => prev - 1)}
                      >
                        Back
                      </Button>
                    )}
                  </Col>
                  <Col className="d-flex justify-content-end gap-4">
                    <Button variant="primary" type="submit" disabled={isSaving}>
                      {steps > 1 ? (isSaving ? id ? "Updating..." : "Saving..." : id ? "Update" : "Save") : "Next"}
                    </Button>
                    <Button
                      onClick={handleCancelClick}
                      variant="secondary"
                      type="button"
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={resultModal.show}
        onHide={() => {
          setResultModal((prev) => ({ ...prev, show: false }));
          if (resultModal.shouldNavigateOnClose) {
            resetFormToInitial();
            navigate("/client");
          }
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className={resultModal.variant === "danger" ? "text-danger" : "text-success"}>
            {resultModal.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{resultModal.message}</Modal.Body>
        <Modal.Footer>
          <Button
            variant={resultModal.variant === "danger" ? "danger" : "success"}
            onClick={() => {
              setResultModal((prev) => ({ ...prev, show: false }));
              if (resultModal.shouldNavigateOnClose) {
                resetFormToInitial();
                navigate("/client");
              }
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmCancelModalOpen}
        onHide={() => setConfirmCancelModalOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Discard changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Save changes will be lost. Are you sure you want to cancel?</Modal.Body>
        <Modal.Footer>
          <Button variant="light" className="border" onClick={() => setConfirmCancelModalOpen(false)}>
            Keep editing
          </Button>
          <Button variant="danger" onClick={confirmCancel}>
            Discard
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AddEditClient;
